import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import {
  Button, CompactError, RobbieMark, RobbieOverlay, RobbiePopup, SegmentedProgress, Spinner,
} from '../../components/ui';
import Section from './Section';
import LoadingStripsGallery from './LoadingStripsGallery';

/**
 * /design?only=loading — every wait primitive: the working mark, busy
 * buttons, the overlay (typical / long / failed), the pop-up (small and
 * summary), compact errors, then strips, StepBar, skeletons and toasts.
 */

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-s3 text-rail-label uppercase text-ink-muted">{children}</p>
);

/** A stand-in step region the overlay covers. */
const Host: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative h-[330px] overflow-hidden rounded-card border border-line bg-surface p-s6">
    <p className="text-card-header text-ink">Supplier Discovery</p>
    <p className="mt-s1 text-body-sm text-ink-secondary">The step content sits under the scrim.</p>
    {children}
  </div>
);

const LoadingGallery: React.FC = () => {
  const [overlayOpen, setOverlayOpen] = useState(true);
  const [popup, setPopup] = useState<'small' | 'summary' | null>(null);
  const [busy, setBusy] = useState(false);
  const retry = { label: 'Try again', onClick: () => undefined };
  const back = { label: 'Go back', onClick: () => undefined };

  return (
    <>
      <Section title="Loading — working mark + busy buttons">
        <div className="flex flex-wrap items-end gap-s7">
          {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((size) => (
            <div key={size} className="flex flex-col items-center gap-s2">
              <RobbieMark state="working" size={size} />
              <span className="font-mono text-meta text-ink-muted">{size}</span>
            </div>
          ))}
        </div>
        <div className="mt-s7 flex flex-wrap items-center gap-s4">
          <Button loading={busy} loadingLabel="Saving…" onClick={() => setBusy(true)} iconRight={<ArrowRight />}>
            Continue to validation
          </Button>
          <Button variant="secondary" loading={busy} loadingLabel="Checking…">Check credits</Button>
          <Button variant="ghost" loading={busy}>Retry</Button>
          <Button variant="secondary" size="sm" onClick={() => setBusy((b) => !b)}>
            Toggle busy
          </Button>
          <Spinner tone="robbie" />
          <Spinner size="sm" />
        </div>
      </Section>

      <Section title="Loading — Robbie overlay">
        <div className="grid grid-cols-1 gap-s6 lg:grid-cols-3">
          <div>
            <Label>Typical</Label>
            <Host>
              <RobbieOverlay
                open={overlayOpen}
                title="Going through 9 components"
                sub="Working out which specs I still need from you"
                usual="Usually under a minute"
                leave
              />
            </Host>
            <Button className="mt-s3" variant="secondary" size="sm" onClick={() => setOverlayOpen((o) => !o)}>
              {overlayOpen ? 'Finish (dissolve)' : 'Start again'}
            </Button>
          </div>
          <div>
            <Label>Running long</Label>
            <Host>
              <RobbieOverlay open long title="Reading your BOM" sub="Pulling parts out of 3 sheets" leave />
            </Host>
          </div>
          <div>
            <Label>Failed — same card</Label>
            <Host>
              <RobbieOverlay
                open
                title="Reading your BOM"
                error={
                  <CompactError
                    title="I couldn't read that file"
                    message="Upload it again, or try a CSV."
                    action={retry}
                    secondaryAction={back}
                  />
                }
              />
            </Host>
          </div>
        </div>
      </Section>

      <Section title="Loading — Robbie pop-up">
        <div className="flex gap-s4">
          <Button variant="secondary" onClick={() => setPopup('small')}>Open small (saving)</Button>
          <Button variant="secondary" onClick={() => setPopup('summary')}>Open summary (research)</Button>
        </div>
        <RobbiePopup open={popup === 'small'} size="small" title="Saving your context" sub="One moment" onClose={() => setPopup(null)} />
        <RobbiePopup
          open={popup === 'summary'}
          title="Finding suppliers for 9 components"
          sub={<>About <b className="text-ink">12–16 minutes</b> more</>}
          onClose={() => setPopup(null)}
          actions={
            <>
              <Button variant="secondary" size="lg" onClick={() => setPopup(null)}>Look at the parts so far</Button>
              <Button size="lg" onClick={() => setPopup(null)}>Go to dashboard</Button>
            </>
          }
        >
          <SegmentedProgress thick counts={{ done: 3, running: 4, waiting: 2 }} />
          <p className="flex items-start gap-s3 rounded-card bg-canvas px-s5 py-s4 text-[12.5px] text-ink-body">
            <Check aria-hidden="true" className="mt-[2px] h-[15px] w-[15px] shrink-0 text-robbie" />
            You don&apos;t need to wait here — I&apos;ll email you when they&apos;re all done.
          </p>
        </RobbiePopup>
      </Section>

      <Section title="Loading — compact errors">
        <div className="grid grid-cols-1 gap-s7 lg:grid-cols-3">
          <div>
            <Label>Stacked</Label>
            <CompactError title="Research stopped" message="I lost the connection mid-way." action={retry} secondaryAction={back} />
          </div>
          <div>
            <Label>Row</Label>
            <CompactError layout="row" title="2 parts didn't finish" message="I'll retry just those." action={retry} />
          </div>
          <div>
            <Label>Tiny</Label>
            <CompactError layout="tiny" title="Failed" action={{ label: 'Retry', onClick: () => undefined }} />
          </div>
        </div>
      </Section>

      <LoadingStripsGallery />
    </>
  );
};

export default LoadingGallery;
